import React, { useEffect, useState } from 'react';
import Image from 'next/image';
import Link from 'next/link';
import { useRouter } from 'next/router';
import { useDispatch, useSelector } from 'react-redux';
import { IconButton, Avatar } from '@material-ui/core';
import clsx from 'clsx';

import { closeModal, openModal } from 'features/utilsSlice';
import { getRequest } from 'utils/api';
import { trackAhoyEvent } from 'utils/ahoy';
import { getButtonUrl } from 'utils/helpers';
import useProfileOwner from 'hooks/useProfileOwner';
import NoAvatarImage from 'public/images/no-avatar.svg';
import EditIcon from 'public/icons/edit-purple.svg';
import RgbButton from 'components/RgbButton';
import WhiteLogo from 'public/images/logo-white.svg';
import DarkLogo from 'public/images/logo-dark.svg';
import ConfirmationDialog from 'components/ConfirmationDialog';
import AnimateList from 'components/animations/AnimateList';
import AnimateListItem from 'components/animations/AnimateListItem';
import styles from 'styles/components/MyLinks.module.scss';

const size = { width: 120, height: 120 };
const avatarFullSize = { width: '100%', height: '100%' };

const redirectUrl = url => {
  if (url.startsWith('http://') || url.startsWith('https://')) {
    return url;
  }

  return `https://${url}`;
};

const ProfileLinks = ({ profile, preview = false }) => {
  const [socialLinks, setSocialLinks] = useState([]);
  const [linkButtons, setLinkButtons] = useState([]);
  const [openConfirmation, setOpenConfirmation] = useState(false);
  const [button, setButton] = useState(null);
  const router = useRouter();
  const dispatch = useDispatch();
  const { username } = router.query;

  const darkDefault = profile?.link_page_layout === 'dark-default';

  const isLoggedIn = useSelector(state => state.auth.loggedIn);

  const profileOwner = useProfileOwner(profile);

  const handleButtonClick = button => {
    if (button.adult) {
      setOpenConfirmation(true);
      dispatch(openModal({ name: 'Confirmation', data: { action: 'approve' } }));
      setButton(button);
    } else {
      const url = getButtonUrl(button, username);

      trackAhoyEvent('Visit Profile Link', {
        profile_link_id: button.id,
        public_profile: button.public_profile,
        wishlist_profile: button.wishlist_profile,
        fan_profile: button.fan_profile,
        url,
      });

      window.open(redirectUrl(url), '_self');
    }
  };

  const handleApprove = button => {
    const url = getButtonUrl(button, username);

    trackAhoyEvent('Visit Profile Link', {
      profile_link_id: button.id,
      public_profile: button.public_profile,
      wishlist_profile: button.wishlist_profile,
      fan_profile: button.fan_profile,
      url,
    });

    window.open(redirectUrl(url), '_self');
    dispatch(closeModal('Confirmation'));
  };

  const getNetworkSrc = link =>
    `/icons/network/white/${link.text
      .replace(/\s/g, '')
      .replace(/[&,\/]/, '_')
      .toLowerCase()}.svg`;

  useEffect(() => {
    getRequest({ endpoint: `profiles/${profile.id}/links` }).then(profile => {
      const links = profile.links;
      const socialLinks = links.filter(link => link.network_id && link.active && !link.adult);
      const linkButtons = links.filter(link => !link.network_id && link.active);

      setSocialLinks(socialLinks);
      setLinkButtons(linkButtons);
    });
  }, [profile?.id]);

  return (
    <div
      id="profile-links"
      className={clsx(styles.container, { [styles.darkContainer]: darkDefault })}
      style={
        preview
          ? { minHeight: 'auto', height: '100%', overflow: 'auto', paddingBottom: 20 }
          : { paddingBottom: 20 }
      }
    >
      <div>
        <Image
          className={styles.imageBackground}
          src={profile.avatar_url}
          layout="fill"
          alt="Background"
          unoptimized
        />
        <div className={styles.cover} />

        {profileOwner && isLoggedIn && !preview && (
          <Link href={`${username}/edit`} passHref>
            <IconButton className={styles.editIcon}>
              <EditIcon width={20} height={20} />
            </IconButton>
          </Link>
        )}

        <div className={styles.profileInfo}>
          <Link href={`/u/${profile.username}`} passHref>
            <Avatar src={profile?.avatar_url} alt="Profile" style={size} className={styles.avatar}>
              <NoAvatarImage style={avatarFullSize} />
            </Avatar>
          </Link>

          <h3>{profile?.display_name}</h3>

          <div
            className={clsx(styles.networks, { [styles.noMarginRight]: socialLinks.length === 1 })}
          >
            <AnimateList key={socialLinks[0]?.id}>
              {socialLinks.map((link, index) => (
                <div onClick={() => handleButtonClick(link)} key={link.id}>
                  <AnimateListItem>
                    <div
                      className={clsx(styles.network, {
                        [styles.lastIcon]: index === socialLinks.length - 1,
                      })}
                    >
                      <Image
                        src={getNetworkSrc(link)}
                        alt={link.text}
                        width={20}
                        height={20}
                        unoptimized
                      />
                    </div>
                  </AnimateListItem>
                </div>
              ))}
            </AnimateList>
          </div>
        </div>

        <div className={styles.links} style={preview ? { width: '100%' } : {}}>
          <AnimateList key={linkButtons[0]?.id}>
            {linkButtons.map(button => (
              <div style={{ marginBottom: 20 }} key={button.id}>
                <AnimateListItem>
                  <RgbButton
                    text={button.text}
                    textColor={button.text_color}
                    backgroundColor={button.bg_color}
                    borderColor={button.text_color}
                    border={button.border}
                    pulse={button.pulse}
                    rounded={button.rounded}
                    handleOnClick={() => handleButtonClick(button)}
                  />
                </AnimateListItem>
              </div>
            ))}
          </AnimateList>
        </div>

        {openConfirmation && (
          <ConfirmationDialog
            title="This link leads to an adult content site. Are you over 18?"
            handleApprove={() => handleApprove(button)}
          />
        )}
      </div>

      <Link href="/" passHref>
        <div className={styles.logoIcon}>{darkDefault ? <WhiteLogo /> : <DarkLogo />}</div>
      </Link>
    </div>
  );
};

export default ProfileLinks;
